import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator, CreditCard, Calendar, TrendingUp, ArrowRight, Percent, Clock, PiggyBank, Download, FileText, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import innotecLogo from "@/assets/innotec-logo.svg";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface FinancialSimulatorProps {
  productId: number;
  productPrice?: number | string;
  className?: string;
  productName?: string;
  productSpecs?: Record<string, string>;
  productImageUrl?: string;
  productDescription?: string;
  onClose?: () => void;
}

export default function FinancialSimulator({
  productId,
  productPrice,
  className = "",
  productName = "Producto",
  productSpecs = {},
  productImageUrl,
  productDescription = "",
  onClose
}: FinancialSimulatorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedPrice, setFetchedPrice] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("plans");
  const [isOpen, setIsOpen] = useState(true);

  // Logs para depuración
  console.log("FinancialSimulator renderizado:", { productId, productName, productPrice, isOpen });

  // Función para cerrar el modal
  const handleClose = () => {
    console.log("Cerrando simulador financiero");
    setIsOpen(false);
    if (onClose) {
      setTimeout(() => {
        onClose();
      }, 300); // Esperar a que termine la animación
    }
  };

  // Obtener el precio del producto desde la Edge Function
  useEffect(() => {
    const fetchProductPrice = async () => {
      // Si ya tenemos un precio proporcionado, no necesitamos hacer la solicitud
      if (productPrice !== undefined) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`https://jlufgkgssnxumguvknhc.functions.supabase.co/product-price/${productId}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error al obtener el precio del producto");
        }

        const data = await response.json();

        if (data.success && data.price) {
          setFetchedPrice(data.price);
        } else {
          throw new Error("No se pudo obtener el precio del producto");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error desconocido");
        console.error("Error al obtener el precio del producto:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductPrice();
  }, [productId, productPrice]);

  // Determinar el precio a utilizar (proporcionado o obtenido de la API)
  const priceToUse = productPrice !== undefined ? productPrice : fetchedPrice;

  // Convertir el precio a número si viene como string
  const basePrice = typeof priceToUse === 'string'
    ? parseFloat(priceToUse.replace(/[^\d.]/g, '')) || 0
    : priceToUse || 0;

  // Estados para los parámetros de financiación
  const [initialPayment, setInitialPayment] = useState(30); // Porcentaje de pago inicial
  const [term, setTerm] = useState(36); // Plazo en meses
  const [interestRate, setInterestRate] = useState(12); // Tasa de interés anual

  // Estados calculados
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // Opciones predefinidas de financiación
  const financingOptions = [
    { name: "Estándar", initialPayment: 30, term: 36, interestRate: 12, icon: <Calculator className="w-5 h-5" /> },
    { name: "Corto plazo", initialPayment: 40, term: 24, interestRate: 10, icon: <Clock className="w-5 h-5" /> },
    { name: "Largo plazo", initialPayment: 20, term: 48, interestRate: 14, icon: <PiggyBank className="w-5 h-5" /> },
    { name: "Personalizado", initialPayment: initialPayment, term: term, interestRate: interestRate }
  ];

  // Calcular los pagos mensuales y otros valores
  useEffect(() => {
    if (basePrice <= 0) return;

    const initialPaymentAmount = (basePrice * initialPayment) / 100;
    const loanAmount = basePrice - initialPaymentAmount;
    const monthlyInterestRate = interestRate / 100 / 12;

    // Fórmula de amortización para calcular el pago mensual
    const monthlyPaymentValue = loanAmount * (
      (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, term)) /
      (Math.pow(1 + monthlyInterestRate, term) - 1)
    );

    const totalPayment = monthlyPaymentValue * term;
    const totalInterestValue = totalPayment - loanAmount;

    setMonthlyPayment(monthlyPaymentValue);
    setTotalInterest(totalInterestValue);
    setTotalAmount(totalPayment + initialPaymentAmount);
  }, [basePrice, initialPayment, term, interestRate]);

  // Función para formatear valores monetarios
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Función para calcular el pago mensual
  const calculateMonthlyPayment = (
    price: number,
    initialPaymentPercent: number,
    termMonths: number,
    interestRatePercent: number
  ): number => {
    const initialPaymentAmount = (price * initialPaymentPercent) / 100;
    const loanAmount = price - initialPaymentAmount;
    const monthlyInterestRate = interestRatePercent / 100 / 12;

    // Fórmula de amortización para calcular el pago mensual
    return loanAmount * (
      (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, termMonths)) /
      (Math.pow(1 + monthlyInterestRate, termMonths) - 1)
    );
  };

  // Función para generar y descargar la ficha técnica en PDF
  const downloadTechnicalSheet = () => {
    try {
      // Crear un nuevo documento PDF
      const doc = new jsPDF();

      // Añadir logo de Innotec
      const logoImg = document.getElementById('innotec-logo-for-pdf') as HTMLImageElement;
      if (logoImg && logoImg.complete) {
        try {
          // Convertir SVG a imagen para PDF
          const canvas = document.createElement('canvas');
          canvas.width = logoImg.naturalWidth || 200;
          canvas.height = logoImg.naturalHeight || 80;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(logoImg, 0, 0, canvas.width, canvas.height);
            const imgData = canvas.toDataURL('image/png');
            // Posicionar el logo en la esquina superior izquierda
            doc.addImage(imgData, 'PNG', 15, 10, 40, 15);
          }
        } catch (error) {
          console.error("Error al añadir el logo al PDF:", error);
        }
      }

      // Añadir título
      doc.setFontSize(22);
      doc.setTextColor(252, 170, 13); // Color FCAA0D
      doc.text("FICHA TÉCNICA", 105, 20, { align: "center" });

      // Añadir información del producto
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text(productName, 105, 30, { align: "center" });

      // Añadir imagen del producto si está disponible
      if (productImageUrl) {
        try {
          doc.addImage(productImageUrl, "JPEG", 65, 40, 80, 60);
        } catch (error) {
          console.error("Error al añadir la imagen al PDF:", error);
        }
      }

      // Añadir descripción del producto si está disponible
      let currentY = productImageUrl ? 110 : 50;

      if (productDescription) {
        doc.setFontSize(14);
        doc.setTextColor(252, 170, 13);
        doc.text("Descripción", 20, currentY);

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);

        // Dividir la descripción en párrafos para mejor legibilidad
        const splitDescription = doc.splitTextToSize(productDescription, 170);
        doc.text(splitDescription, 20, currentY + 10);

        currentY += 10 + (splitDescription.length * 7); // Ajustar posición Y basado en el número de líneas
      }

      // Extraer y mostrar características principales (primeras 3-5 especificaciones)
      const specsEntries = Object.entries(productSpecs);
      if (specsEntries.length > 0) {
        currentY += 10;
        doc.setFontSize(14);
        doc.setTextColor(252, 170, 13);
        doc.text("Características Principales", 20, currentY);

        // Mostrar las primeras 5 características como puntos destacados
        const mainFeatures = specsEntries.slice(0, Math.min(5, specsEntries.length));

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);

        mainFeatures.forEach((feature, index) => {
          const [key, value] = feature;
          doc.text(`• ${key}: ${value}`, 20, currentY + 10 + (index * 7));
        });

        currentY += 10 + (mainFeatures.length * 7);
      }

      // Añadir información de financiación
      currentY += 10;

      doc.setFontSize(14);
      doc.setTextColor(252, 170, 13);
      doc.text("Detalles de Financiación", 20, currentY);

      // Crear tabla de financiación
      autoTable(doc, {
        startY: currentY + 5,
        head: [['Concepto', 'Valor']],
        body: [
          ['Precio base', formatCurrency(basePrice)],
          ['Pago inicial (' + initialPayment + '%)', formatCurrency(basePrice * initialPayment / 100)],
          ['Importe a financiar', formatCurrency(basePrice * (1 - initialPayment / 100))],
          ['Plazo', term + ' meses'],
          ['Tasa de interés', interestRate + '%'],
          ['Cuota mensual', formatCurrency(monthlyPayment)],
          ['Intereses totales', formatCurrency(totalInterest)],
          ['Coste total', formatCurrency(totalAmount)]
        ],
        theme: 'grid',
        headStyles: {
          fillColor: [252, 170, 13],
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });

      // Añadir pie de página
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);
        doc.text(
          "* Esta simulación es orientativa. Consulta con nuestro equipo para obtener una oferta personalizada.",
          105,
          285,
          { align: "center" }
        );
        doc.text(
          "Innotec Maquinaria - " + new Date().toLocaleDateString("es-ES"),
          105,
          290,
          { align: "center" }
        );
      }

      // Guardar el PDF
      doc.save(`Ficha_Tecnica_${productName.replace(/\s+/g, '_')}.pdf`);
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      alert("Ha ocurrido un error al generar la ficha técnica. Por favor, inténtelo de nuevo.");
    }
  };

  // Función para aplicar una opción predefinida
  const applyFinancingOption = (option: typeof financingOptions[0]) => {
    setInitialPayment(option.initialPayment);
    setTerm(option.term);
    setInterestRate(option.interestRate);
    setActiveTab("custom");
  };

  // Si está cargando, mostrar un indicador de carga dentro del modal
  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-zinc-900 max-w-4xl border-zinc-800 z-[99999]">
          <DialogHeader className="flex justify-between items-center">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#FCAA0D]" />
              Simulador de Financiación
            </DialogTitle>
            <button
              onClick={handleClose}
              className="rounded-full p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogHeader>
          <div className="flex items-center justify-center h-60">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FCAA0D]/20"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-l-4 border-[#FCAA0D] absolute top-0 left-0"></div>
              <Calculator className="w-6 h-6 text-[#FCAA0D] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
          <p className="text-center text-zinc-400 mt-4 animate-pulse">Cargando simulador de financiación...</p>
        </DialogContent>
      </Dialog>
    );
  }

  // Si hay un error, mostrar un mensaje de error dentro del modal
  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-zinc-900 max-w-4xl border-zinc-800 z-[99999]">
          <DialogHeader className="flex justify-between items-center">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#FCAA0D]" />
              Simulador de Financiación
            </DialogTitle>
            <button
              onClick={handleClose}
              className="rounded-full p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogHeader>
          <div className="text-red-400 p-6 bg-red-500/10 rounded-xl border border-red-500/20 shadow-inner">
            <p className="font-medium mb-2">Error al cargar el simulador:</p>
            <p>{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 w-full py-3 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium transition-colors flex items-center justify-center gap-2"
          >
            <span>Reintentar</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  // Si no hay precio disponible, mostrar un mensaje dentro del modal
  if (basePrice <= 0) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-zinc-900 max-w-4xl border-zinc-800 z-[99999]">
          <DialogHeader className="flex justify-between items-center">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#FCAA0D]" />
              Simulador de Financiación
            </DialogTitle>
            <button
              onClick={handleClose}
              className="rounded-full p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogHeader>
          <div className="text-zinc-400 p-6 bg-zinc-800/70 rounded-xl border border-zinc-700/50 shadow-inner">
            <p className="font-medium mb-2 text-white">Información no disponible</p>
            <p>No hay información de precio disponible para este producto.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-zinc-900 max-w-4xl border-zinc-800 p-0 z-[99999]">
        <DialogHeader className="flex justify-between items-center p-6">
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#FCAA0D]" />
            Simulador de Financiación
          </DialogTitle>
          <button
            onClick={handleClose}
            className="rounded-full p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </DialogHeader>

        <div className="p-6 pt-0 max-h-[80vh] overflow-y-auto">
          {/* Logo oculto para PDF */}
          <img id="innotec-logo-for-pdf" src={innotecLogo} alt="Logo de Innotec" style={{ display: 'none' }} />

          <div className="bg-zinc-800/50 p-4 rounded-lg mb-6 md:mb-8 border border-zinc-700/30 shadow-inner">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-lg font-semibold text-white">{productName}</h4>
                <p className="text-zinc-400 text-sm">
                  {productDescription.substring(0, 100)}{productDescription.length > 100 ? '...' : ''}
                </p>
              </div>
              <span className="text-[#FCAA0D] font-bold text-xl">{formatCurrency(basePrice)}</span>
            </div>
          </div>

          <Tabs defaultValue="plans" className="w-full">
            <TabsList className="grid grid-cols-2 mb-6 bg-zinc-800/50 p-1 rounded-lg">
              <TabsTrigger value="plans" className="data-[state=active]:bg-[#FCAA0D] data-[state=active]:text-zinc-900">
                Planes Recomendados
              </TabsTrigger>
              <TabsTrigger value="custom" className="data-[state=active]:bg-[#FCAA0D] data-[state=active]:text-zinc-900">
                Personalizar Plan
              </TabsTrigger>
            </TabsList>

            <TabsContent value="plans">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {financingOptions.slice(0, 3).map((option, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    onClick={() => applyFinancingOption(option)}
                    className="bg-zinc-800/70 rounded-xl p-5 border border-zinc-700/30 hover:border-[#FCAA0D]/30 transition-colors shadow-md cursor-pointer"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-full bg-[#FCAA0D]/10">
                        {option.icon}
                      </div>
                      <h5 className="text-white font-medium">{option.name}</h5>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400 text-sm">Pago inicial:</span>
                        <span className="text-white">{option.initialPayment}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400 text-sm">Plazo:</span>
                        <span className="text-white">{option.term} meses</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400 text-sm">Interés:</span>
                        <span className="text-white">{option.interestRate}%</span>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-zinc-700/30">
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400 text-sm">Mensualidad:</span>
                        <span className="text-[#FCAA0D] font-bold">
                          {formatCurrency(
                            calculateMonthlyPayment(
                              basePrice,
                              option.initialPayment,
                              option.term,
                              option.interestRate
                            )
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="custom">
              <div className="space-y-6 bg-zinc-800/50 p-4 md:p-6 rounded-xl border border-zinc-700/30 shadow-inner">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-full bg-[#FCAA0D]/10">
                        <Percent className="w-4 h-4 text-[#FCAA0D]" />
                      </div>
                      <Label htmlFor="initialPayment" className="text-white font-medium">
                        Pago inicial ({initialPayment}%)
                      </Label>
                    </div>
                    <span className="text-[#FCAA0D] font-bold text-lg">{formatCurrency(basePrice * initialPayment / 100)}</span>
                  </div>
                  <div className="pl-10">
                    <Slider
                      id="initialPayment"
                      min={10}
                      max={50}
                      step={5}
                      value={[initialPayment]}
                      onValueChange={(value) => setInitialPayment(value[0])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-zinc-500 mt-1 px-2">
                      <span>10%</span>
                      <span>30%</span>
                      <span>50%</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-full bg-[#FCAA0D]/10">
                        <Calendar className="w-4 h-4 text-[#FCAA0D]" />
                      </div>
                      <Label htmlFor="term" className="text-white font-medium">
                        Plazo ({term} meses)
                      </Label>
                    </div>
                    <span className="text-zinc-300 bg-zinc-800 px-3 py-1 rounded-full text-sm">
                      {Math.floor(term / 12)} años y {term % 12} meses
                    </span>
                  </div>
                  <div className="pl-10">
                    <Slider
                      id="term"
                      min={12}
                      max={60}
                      step={12}
                      value={[term]}
                      onValueChange={(value) => setTerm(value[0])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-zinc-500 mt-1 px-2">
                      <span>12 meses</span>
                      <span>36 meses</span>
                      <span>60 meses</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-full bg-[#FCAA0D]/10">
                        <TrendingUp className="w-4 h-4 text-[#FCAA0D]" />
                      </div>
                      <Label htmlFor="interestRate" className="text-white font-medium">
                        Tasa de interés ({interestRate}%)
                      </Label>
                    </div>
                    <span className="text-zinc-300 bg-zinc-800 px-3 py-1 rounded-full text-sm">
                      TAE: {(interestRate * 1.1).toFixed(2)}%
                    </span>
                  </div>
                  <div className="pl-10">
                    <Slider
                      id="interestRate"
                      min={6}
                      max={18}
                      step={0.5}
                      value={[interestRate]}
                      onValueChange={(value) => setInterestRate(value[0])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-zinc-500 mt-1 px-2">
                      <span>6%</span>
                      <span>12%</span>
                      <span>18%</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Resumen de financiación */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-6 md:mt-8 pt-6 border-t border-zinc-700/30"
          >
            <div className="flex items-center gap-2 mb-4 md:mb-6">
              <h4 className="text-white font-semibold text-lg">Resumen de financiación</h4>
              <div className="flex-1 h-px bg-gradient-to-r from-[#FCAA0D]/30 to-transparent"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-zinc-800/70 rounded-xl p-5 border border-zinc-700/30 hover:border-[#FCAA0D]/30 transition-colors shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full bg-[#FCAA0D]/10">
                    <CreditCard className="w-5 h-5 text-[#FCAA0D]" />
                  </div>
                  <span className="text-zinc-400 text-sm">Mensualidad</span>
                </div>
                <div className="text-white font-bold text-2xl">
                  {formatCurrency(monthlyPayment)}
                </div>
              </div>

              <div className="bg-zinc-800/70 rounded-xl p-5 border border-zinc-700/30 hover:border-[#FCAA0D]/30 transition-colors shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full bg-[#FCAA0D]/10">
                    <Calendar className="w-5 h-5 text-[#FCAA0D]" />
                  </div>
                  <span className="text-zinc-400 text-sm">Plazo</span>
                </div>
                <div className="text-white font-bold text-2xl">
                  {term} meses
                </div>
              </div>

              <div className="bg-zinc-800/70 rounded-xl p-5 border border-zinc-700/30 hover:border-[#FCAA0D]/30 transition-colors shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full bg-[#FCAA0D]/10">
                    <TrendingUp className="w-5 h-5 text-[#FCAA0D]" />
                  </div>
                  <span className="text-zinc-400 text-sm">Interés total</span>
                </div>
                <div className="text-white font-bold text-2xl">
                  {formatCurrency(totalInterest)}
                </div>
              </div>
            </div>

            <div className="bg-zinc-800/70 rounded-xl p-6 border border-zinc-700/30 shadow-md">
              <div className="flex justify-between items-center mb-3">
                <span className="text-zinc-400">Precio base:</span>
                <span className="text-white font-medium">{formatCurrency(basePrice)}</span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-zinc-400">Pago inicial ({initialPayment}%):</span>
                <span className="text-white font-medium">{formatCurrency(basePrice * initialPayment / 100)}</span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-zinc-400">Importe a financiar:</span>
                <span className="text-white font-medium">{formatCurrency(basePrice * (1 - initialPayment / 100))}</span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-zinc-400">Intereses totales:</span>
                <span className="text-white font-medium">{formatCurrency(totalInterest)}</span>
              </div>
              <div className="flex justify-between items-center p-4 mt-3 bg-[#FCAA0D]/10 rounded-lg border border-[#FCAA0D]/20">
                <span className="text-white font-semibold">Coste total:</span>
                <span className="text-[#FCAA0D] font-bold text-xl">{formatCurrency(totalAmount)}</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-zinc-500 text-sm">
                * Esta simulación es orientativa. Consulta con nuestro equipo para obtener una oferta personalizada.
              </p>
            </div>

            {/* Botón para descargar ficha técnica */}
            <button
              onClick={downloadTechnicalSheet}
              className="mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold transition-colors"
            >
              <FileText className="w-5 h-5" />
              <span>Descargar Ficha Técnica</span>
              <Download className="w-4 h-4 ml-1" />
            </button>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
